import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faPlus, faTrash, faTag } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { showSuccessToast, handleAPIError } from '@/utils/toasts';
import type { JournalTag, JournalTagCategory } from '@/models';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import EmptyState from '@/components/ui/EmptyState';
import ErrorState from '@/components/ui/ErrorState';
import Modal from '@/components/ui/Modal';
import {
  JOURNAL_FIELD_CLASS,
  JOURNAL_LABEL_CLASS,
  TAG_CATEGORY_LABELS,
  TAG_CATEGORY_OPTIONS,
  tagCategoryBadgeClass,
} from '@/components/journal/journalHelpers';
import { useJournalResource } from '@/components/journal/useJournalResource';

const UMLAUT_REPLACEMENTS: Record<string, string> = { 'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'ß': 'ss' };

// Umlauts are transliterated first, otherwise "Zu früh" would slug to "zu-fr-h".
function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[äöüß]/g, (c) => UMLAUT_REPLACEMENTS[c])
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function JournalTagsPage() {
  const { data, loading, error, reload } = useJournalResource(
    () => APIRoutes.listJournalTags(),
    [],
    'Fehler beim Laden der Tags',
  );
  const tags: JournalTag[] = data?.tags ?? [];
  const [busyAction, setBusyAction] = useState<'create' | 'delete' | null>(null);
  const busy = busyAction !== null;
  const [deleteTarget, setDeleteTarget] = useState<JournalTag | null>(null);

  const [name, setName] = useState('');
  const [category, setCategory] = useState<JournalTagCategory>('GENERAL');
  const [color, setColor] = useState('#3b82f6');

  // An all-punctuation name slugs to "" - the backend rejects that, so block it here.
  const slug = slugify(name);

  const handleCreate = async () => {
    if (busy || slug === '') return;
    setBusyAction('create');
    try {
      await APIRoutes.createJournalTag({ slug, name: name.trim(), category, color });
      setName('');
      showSuccessToast('Tag erstellt');
      reload();
    } catch (err) {
      handleAPIError(err, 'Tag konnte nicht erstellt werden');
    } finally {
      setBusyAction(null);
    }
  };

  const handleDelete = async (tag: JournalTag) => {
    setBusyAction('delete');
    try {
      await APIRoutes.deleteJournalTag(tag.id);
      setDeleteTarget(null);
      showSuccessToast('Tag gelöscht');
      reload();
    } catch (err) {
      handleAPIError(err, 'Tag konnte nicht gelöscht werden');
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div className="space-y-4">
      <Card title="Neuen Tag anlegen">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[12rem]">
            <label className={JOURNAL_LABEL_CLASS} htmlFor="tag-name">Name</label>
            <input
              id="tag-name"
              className={`${JOURNAL_FIELD_CLASS} w-full disabled:opacity-60`}
              value={name}
              disabled={busy}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void handleCreate(); }}
              placeholder="z.B. Zu früh geschlossen"
            />
          </div>
          <div>
            <label className={JOURNAL_LABEL_CLASS} htmlFor="tag-category">Kategorie</label>
            <select id="tag-category" className={`${JOURNAL_FIELD_CLASS} cursor-pointer disabled:opacity-60`} value={category} disabled={busy} onChange={(e) => setCategory(e.target.value as JournalTagCategory)}>
              {TAG_CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>{TAG_CATEGORY_LABELS[c]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={JOURNAL_LABEL_CLASS} htmlFor="tag-color">Farbe</label>
            <input id="tag-color" aria-label="Tag-Farbe" className="h-10 w-14 rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer disabled:opacity-60" type="color" value={color} disabled={busy} onChange={(e) => setColor(e.target.value)} />
          </div>
          <Button icon={faPlus} loading={busyAction === 'create'} disabled={busy || slug === ''} onClick={handleCreate}>
            Anlegen
          </Button>
        </div>
      </Card>

      {loading ? (
        <div className="text-center py-16" role="status" aria-label="Lädt">
          <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      ) : error !== null ? (
        <ErrorState message={error} onRetry={reload} />
      ) : tags.length === 0 ? (
        <Card>
          <EmptyState
            icon={faTag}
            title="Noch keine Tags"
            description="Lege oben deinen ersten Tag an, um Trades nach Fehlern, Regeln und eigenen Kategorien zu ordnen."
          />
        </Card>
      ) : (
        <Card padded={false} bodyClassName="divide-y divide-gray-100 dark:divide-gray-700/50">
          {tags.map((tag) => (
            <div key={tag.id} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: tag.color ?? '#9ca3af' }} />
                <span className="font-medium text-gray-900 dark:text-white truncate">{tag.name}</span>
                <Badge className="shrink-0" colorClass={tagCategoryBadgeClass(tag.category)}>{TAG_CATEGORY_LABELS[tag.category]}</Badge>
              </div>
              <Button
                variant="ghost"
                size="sm"
                icon={faTrash}
                title={`Tag "${tag.name}" löschen`}
                aria-label={`Tag "${tag.name}" löschen`}
                disabled={busy}
                onClick={() => setDeleteTarget(tag)}
              />
            </div>
          ))}
        </Card>
      )}

      <Modal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Tag löschen"
        maxWidth="md"
        closeDisabled={busy}
      >
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {deleteTarget !== null && `Tag "${deleteTarget.name}" wirklich löschen? Er wird von allen Trades entfernt, an denen er hängt.`}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" disabled={busy} onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
            <Button
              variant="danger"
              loading={busyAction === 'delete'}
              onClick={() => { if (deleteTarget !== null) void handleDelete(deleteTarget); }}
            >
              Löschen
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
